import { useContext, useEffect, useState } from 'react';
import { DataContext, Trade } from "./dataProvider";
import { isEqual } from 'lodash';

const useTrades = (figi: string | undefined, tradeCount: number) => {
  const { send, trades: allTrades } = useContext(DataContext);

  // Subscribe and fetch the opening window in ONE message. They used to be two `send` calls, which
  // are not ordered: `send` awaits `getToken()` first, and inside the token refresh window
  // (TOKEN_REFRESH_WINDOW.earliest, 90s before expiry) each call starts its own Cognito
  // refreshSession, so the fetch could reach the server ahead of the subscribe. The server honours
  // whatever order arrives, and a trade appended between the fetch's read and the subscription's
  // registration reaches nobody -- under delta publishing it is never re-sent, so it stays missing
  // until that key next changes, which for a trade that never collapses is never. (The positional
  // protocol tended to repair this by accident, when a later batch's `start` rewound past it.)
  //
  // One message removes the race rather than narrowing it: the server registers the subscription
  // before it reads the index window, and holds the history lock across both.
  useEffect(() => {
    if (figi && send) {
      send({trade: [{
        figi,
        include_inference: true,
        subscribe: true,
        // a falsy tradeCount still subscribes, it just opens with no history
        ...(tradeCount ? { index: Array(tradeCount).fill(null).map((_, i) => i - tradeCount) } : {}),
      }]});
      return () => { send({trade: [{figi: figi, include_inference: true, unsubscribe: true}]}, false); }
    }
  }, [figi, tradeCount, send]);

  const [trades, setTrades] = useState<Trade[]>([]);
  useEffect(() => {
    const newTrades = ((figi && allTrades[figi]) || []).slice(-tradeCount);

    setTrades(prev => {
      return isEqual(prev, newTrades) ? prev : newTrades;
    })
  }, [allTrades, figi, tradeCount]);

  return trades;
}

export default useTrades;
